import { useEffect, useState } from "react";
import type { Authorization, CustomerList, CustomerRow } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { CustomerDialog } from "./CustomerDialog";
import { formatPhone } from "../../lib/phone";

interface Props {
  /** The owner's Beheer code. */
  authorization: Authorization;
}

type Sort = "count" | "name" | "zone";

const number = (value: number) => value.toLocaleString("nl-BE");

/**
 * Beheer › Klanten: the number of customers and per municipality, and a customer to look at or change
 * after a search on name, phone, street or e-mail (from 3 characters, at most 10 found), in three ways
 * sorted. No list of all customers and no export (Furkan, 11/10/2026: a shop that leaves Tafo does not
 * take the customers with it). There is no delete: customers are never deleted automatically.
 */
export function CustomersScreen({ authorization }: Props) {
  const { api, promotions, report, customersRevision } = useTill();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<Sort>("count");
  const [list, setList] = useState<CustomerList | null>(null);
  const [open, setOpen] = useState<CustomerRow | null>(null);
  const [revision, setRevision] = useState(0);
  const text = t.customersAdmin;
  const goal = promotions?.points.goal ?? 11;

  // also again when a customer changed on any device (a new number on a ticket, an import)
  useEffect(() => {
    let current = true;
    const params = new URLSearchParams({ sort });
    if (query.trim()) params.set("q", query.trim());
    // a short pause while typing, so each letter does not ask the server
    const timer = setTimeout(() => {
      api
        .request<CustomerList>("GET", `/device/customers?${params.toString()}`, {
          staffToken: authorization.token,
        })
        .then((answer) => {
          if (current) setList(answer);
        })
        .catch(report);
    }, 150);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [api, authorization.token, query, sort, report, revision, customersRevision]);

  return (
    <div className="panel admin-section customers-admin">
      <h2>{text.title}</h2>
      {list && (
        <>
          <div className="rowacts">
            <b className="num">{text.count(number(list.total))}</b>
            <span className="note">
              {list.byZone.map((zone) => `${zone.zone || "?"} ${String(zone.count)}`).join(" · ")}
            </span>
          </div>
        </>
      )}
      <div className="rowacts">
        <input
          className="cusearch"
          placeholder={text.search}
          aria-label={text.search}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <div className="seg" role="group" aria-label={text.sortLabel}>
          {(["count", "name", "zone"] as const).map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={sort === key}
              onClick={() => {
                setSort(key);
              }}
            >
              {text.sort[key]}
            </button>
          ))}
        </div>
      </div>
      {query.trim().length < 3 ? (
        <p className="note">{text.searchFirst}</p>
      ) : (
        <div className="tablewrap">
          <table className="stafftbl stack customers">
            <thead>
              <tr>
                <th>{text.columns.name}</th>
                <th>{text.columns.phone}</th>
                <th>{text.columns.address}</th>
                <th className="r">{text.columns.orders}</th>
                <th>{text.columns.points}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list?.customers.map((customer) => (
                <tr key={customer.id}>
                  <td className="full">
                    <b>{customer.name || "—"}</b>
                    {customer.note && (
                      <>
                        {" "}
                        <span className="pill warn" title={customer.note}>
                          {text.note}
                        </span>
                      </>
                    )}
                    {customer.newsletterPending && (
                      <>
                        {" "}
                        <span className="pill">{text.newsletterPending}</span>
                      </>
                    )}
                    {customer.email && (
                      <>
                        <br />
                        <span className="note">{customer.email}</span>
                      </>
                    )}
                  </td>
                  <td className="num" data-label={text.columns.phone}>
                    {formatPhone(customer.phone)}
                  </td>
                  <td className="grow" data-label={text.columns.address}>
                    {[`${customer.street} ${customer.number}`.trim(), customer.zone].filter((part) => part).join(", ")}
                  </td>
                  <td className="r num" data-label={text.columns.orders}>
                    {customer.orders}
                  </td>
                  <td className="num" data-label={text.columns.points}>
                    {customer.points}/{goal}
                  </td>
                  <td className="end">
                    <button
                      type="button"
                      className="btn"
                      aria-label={text.editOf(customer.name || formatPhone(customer.phone))}
                      onClick={() => {
                        setOpen(customer);
                      }}
                    >
                      {text.edit}
                    </button>
                  </td>
                </tr>
              ))}
              {list?.customers.length === 0 && (
                <tr>
                  <td colSpan={6} className="note">
                    {text.none}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {list && list.found > list.customers.length && (
        <p className="note">{text.more(list.customers.length, number(list.found))}</p>
      )}
      <p className="note">{text.footer}</p>
      {open && (
        <CustomerDialog
          authorization={authorization}
          customer={open}
          onSaved={() => {
            setOpen(null);
            setRevision((count) => count + 1);
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </div>
  );
}
